import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import backgroundNomeJesss from "../assets/backgrounds/backgroundnomejessslogin.svg";
import backgroundTela from "../assets/backgrounds/backgroundtelalogin.svg";
import linhaBackgroundTop from "../assets/backgrounds/linhabackgroundtoplogin.svg";
import FloatingLanguageSelector from "./FloatingLanguageSelector";

interface LoginLayoutWrapperProps {
  children: React.ReactNode;
  mode?: "login" | "signup" | "passwordRecovery";
}

const LoginLayoutWrapper: React.FC<LoginLayoutWrapperProps> = ({
  children,
  mode = "login",
}) => {
  const { t } = useTranslation();

  return (
    <>
      <FloatingLanguageSelector />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          minHeight: "100vh",
          overflow: { xs: "auto", lg: "hidden" },
          position: "relative",
          backgroundImage: { xs: "none", md: `url(${backgroundTela})` },
          backgroundColor: { xs: "#F5F5F5", md: "transparent" },
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: {
              xs: "none",
              md: "linear-gradient(86deg, rgba(255, 255, 255, 0.15) 0%, rgba(242, 242, 242, 1) 34%)",
            },
            zIndex: 1,
            pointerEvents: "none",
          },
        }}
      >
        {/* Linha decorativa no topo - Esconder no mobile */}
        <Box
          component="img"
          src={linhaBackgroundTop}
          alt=""
          sx={{
            display: { xs: "none", md: "block" },
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            width: "100%",
            height: "auto",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />

        {/* Left Side - Logo e Textos */}
        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            flex: 1,
            minHeight: "100vh",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-end",
            position: "relative",
            px: 6,
            py: 8,
            pr: 8,
            zIndex: 2,
          }}
        >
          {/* Container Logo e Textos */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              maxWidth: "600px",
            }}
          >
            {/* Logo Jesss */}
            <Box
              component="img"
              src={backgroundNomeJesss}
              alt="Jesss"
              sx={{
                width: { xs: "380px", md: "480px" },
                height: "auto",
              }}
            />

            {/* Textos */}
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: 0.8,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: "2.2rem", md: "2.8rem" },
                  color: "#54398E",
                  fontWeight: "700",
                  lineHeight: 1.3,
                }}
              >
                {mode === "signup"
                  ? t("login.welcomeSignup")
                  : mode === "passwordRecovery"
                    ? t("login.welcome")
                    : t("login.welcome")}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1.4rem", md: "1.65rem" },
                  color: "#54398E",
                  fontWeight: "400",
                  lineHeight: 1.5,
                }}
              >
                {mode === "signup"
                  ? t("login.signupSubtitle")
                  : mode === "passwordRecovery"
                    ? t("login.recoverPasswordLeftDescription")
                    : t("login.subtitle")}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Side - Modal Section */}
        <Box
          sx={{
            flex: { xs: 1, lg: 0.85 },
            minHeight: { xs: "auto", lg: "100vh" },
            display: "flex",
            flexDirection: "column",
            justifyContent: { xs: "center", md: "flex-start", lg: "center" },
            alignItems: "center",
            px: { xs: 0, lg: 0 },
            py: { xs: 2, md: 0, lg: 0 },
            position: "relative",
            zIndex: 998,
            "@media (max-height: 450px) and (orientation: landscape)": {
              justifyContent: "flex-start",
              minHeight: "auto",
              py: 1,
            },
          }}
        >
          {/* Logo Jesss - Apenas Tablet (esconder no mobile) */}
          <Box
            className="login-welcome-section"
            sx={{
              display: { xs: "none", md: "flex", lg: "none" },
              flexDirection: "column",
              alignItems: "center",
              mb: { md: 5 },
              mt: { md: 7 },
              textAlign: "center",
              gap: 2,
              px: 3,
            }}
          >
            <Box
              component="img"
              src={backgroundNomeJesss}
              alt="Jesss"
              sx={{
                width: { md: "220px" },
                height: "auto",
              }}
            />
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { md: "1.4rem" },
                  color: "#54398E",
                  fontWeight: "700",
                  mb: 0.3,
                  lineHeight: 1.3,
                }}
              >
                {mode === "signup"
                  ? t("login.welcomeSignup")
                  : mode === "passwordRecovery"
                    ? t("login.welcome")
                    : t("login.welcome")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { md: "1.05rem" },
                  color: "#54398E",
                  fontWeight: "400",
                  lineHeight: 1.5,
                }}
              >
                {mode === "signup"
                  ? t("login.signupSubtitle")
                  : mode === "passwordRecovery"
                    ? t("login.recoverPasswordLeftDescription")
                    : t("login.subtitle")}
              </Typography>
            </Box>
          </Box>

          {/* Modal Section - Baseado no design do Figma */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "600px", lg: "546px" },
              backgroundColor: {
                xs: "transparent",
                md: "rgb(255, 255, 255)",
                lg: "rgb(255, 255, 255)",
              },
              borderRadius: {
                xs: "0",
                md: "24px 24px 0px 0px",
                lg: "32px 32px 0px 0px",
              },
              boxShadow: {
                xs: "none",
                md: "rgba(96, 191, 183, 0.15) 0px 4px 20px 0px",
              },
              padding: {
                xs: "48px 28px 32px",
                sm: "56px 32px 32px",
                md: "48px 48px 40px",
                lg: "48px 40px",
              },
              textAlign: "left",
              minHeight: { xs: "fit-content", md: "fit-content", lg: "70vh" },
              "@media (max-width: 360px)": {
                padding: "44px 24px 28px",
              },
              height: { xs: "auto", lg: "110vh" },
              maxHeight: { xs: "none !important", md: "85vh", lg: "75vh" },
              overflow: { xs: "visible", md: "auto" },
              position: { xs: "relative", md: "relative", lg: "absolute" },
              bottom: { xs: "0", md: "0", lg: "-33vh" },
              left: { xs: "0px", lg: "auto" },
              right: { xs: "0px", lg: "auto" },
              zIndex: 999,
              // Mobile - sem container branco, apenas elementos
              "@media (max-width: 767px)": {
                borderRadius: "0 !important",
                maxWidth: "100% !important",
                margin: "0",
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
              // Tablets e resoluções médias (768px - 1200px) - ajuste específico
              "@media (min-width: 768px) and (max-width: 1199px)": {
                minHeight: "fit-content !important",
                maxHeight: "none !important",
                overflow: "visible !important",
                position: "relative !important",
                bottom: "0 !important",
                marginBottom: "0 !important",
                borderRadius: "24px 24px 0px 0px",
              },
              // Mobile Landscape - altura reduzida
              "@media (max-height: 450px) and (orientation: landscape)": {
                minHeight: "auto !important",
                height: "auto",
                maxHeight: "none !important",
                padding: "16px 20px",
                bottom: "0",
                borderRadius: "16px 16px 0px 0px",
                position: "relative",
              },
              // Resoluções altas (4K+) - limitar largura máxima do formulário
              "@media (min-width: 3840px)": {
                maxWidth: "700px",
                padding: "60px 50px",
              },
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LoginLayoutWrapper;
